<div class="session-end-container">
  <!-- Loading State -->
  <div *ngIf="loading" class="loading-section">
    <p-card>
      <div class="loading-content">
        <p-progressSpinner styleClass="w-4rem h-4rem" strokeWidth="4" animationDuration="1s"></p-progressSpinner>
        <h2>Processing session and recordings...</h2>
        <p class="text-color-secondary mt-2 mb-0">Please wait while we finalize your session data and process any recordings. This may take up to 30 seconds.</p>
      </div>
    </p-card>
  </div>

  <!-- Error State -->
  <div *ngIf="error && !loading" class="error-section">
    <p-card>
      <div class="error-content">
        <p-avatar icon="pi pi-exclamation-triangle" size="large" styleClass="error-avatar"></p-avatar>
        <h2>Unable to Load Session Summary</h2>
        <p-message severity="error" [text]="error"></p-message>
        <div class="mt-4">
          <p class="text-center text-muted">You can safely close this page.</p>
        </div>
      </div>
    </p-card>
  </div>

  <!-- Success State -->
  <div *ngIf="!loading && !error" class="content-section">
    <!-- Header Card -->
    <p-card styleClass="mb-4">
      <div class="header-content">
        <div class="logo-section">
          <h1 class="app-title">GoVideo</h1>
          <p-tag value="Session Completed" severity="success" icon="pi pi-check"></p-tag>
        </div>
        <div class="timestamp">
          <small class="text-muted">Ended:</small>
          <div class="font-semibold">{{ currentDateTime }}</div>
        </div>
      </div>
    </p-card>

    <!-- Role-specific messaging -->
    <p-card styleClass="mb-4">
      <div *ngIf="isCoordinator" class="role-message">
        <div class="flex align-items-center gap-3 mb-3">
          <p-avatar icon="pi pi-user-edit" size="large" styleClass="coordinator-avatar"></p-avatar>
          <h2 class="m-0">Coordinator Session Summary</h2>
        </div>
        <p class="text-color-secondary m-0">Your remote inspection session has been completed successfully. Review the details below.</p>
      </div>
      
      <div *ngIf="isInspector" class="role-message">
        <div class="flex align-items-center gap-3 mb-3">
          <p-avatar icon="pi pi-search" size="large" styleClass="inspector-avatar"></p-avatar>
          <h2 class="m-0">Inspector Session Summary</h2>
        </div>
        <p class="text-color-secondary m-0">Thank you for completing this remote inspection. Your session details are shown below.</p>
      </div>
    </p-card>

    <!-- Session Summary Card -->
    <p-card *ngIf="sessionSummary" styleClass="mb-4">
      <ng-template pTemplate="header">
        <div class="px-3 py-2">
          <h3 class="m-0">Session Details</h3>
        </div>
      </ng-template>
      
      <div class="summary-grid">
        <div class="summary-item">
          <small class="text-muted font-semibold">SESSION ID</small>
          <p-chip [label]="sessionSummary.sessionId" styleClass="font-monospace"></p-chip>
        </div>
        
        <div class="summary-item">
          <small class="text-muted font-semibold">DURATION</small>
          <div class="summary-value">
            <i class="pi pi-clock mr-2"></i>
            {{ sessionSummary.formattedDuration || 'Not available' }}
          </div>
        </div>
        
        <div class="summary-item">
          <small class="text-muted font-semibold">MEDIA CAPTURED</small>
          <div class="media-count">
            <p-badge [value]="sessionSummary.mediaCount" size="large" styleClass="count-badge"></p-badge>
            <span class="ml-2">{{ sessionSummary.mediaCount === 1 ? 'item' : 'items' }}</span>
          </div>
        </div>
        
        <div class="summary-item">
          <small class="text-muted font-semibold">RECORDING STATUS</small>
          <div class="recording-status">
            <p-tag 
              [value]="sessionSummary.hasRecording ? 'Available' : 'No Recording'" 
[severity]="sessionSummary.hasRecording ? 'success' : 'warn'"
              [icon]="sessionSummary.hasRecording ? 'pi pi-check' : 'pi pi-times'">
            </p-tag>
          </div>
        </div>
        
        <div class="summary-item zoom-session" *ngIf="sessionSummary.zoomSessionId">
          <small class="text-muted font-semibold">ZOOM SESSION ID</small>
          <p-chip [label]="sessionSummary.zoomSessionId" styleClass="font-monospace" icon="pi pi-video"></p-chip>
        </div>
      </div>
    </p-card>

    <!-- Next Steps Section -->
    <p-card styleClass="mb-4">
      <ng-template pTemplate="header">
        <div class="px-3 py-2">
          <h3 class="m-0">Next Steps</h3>
        </div>
      </ng-template>
      
      <div class="steps-content" *ngIf="isCoordinator">
        <div class="flex flex-column gap-3">
          <div class="flex align-items-center gap-3">
            <i class="pi pi-check-circle text-green-500"></i>
            <span>The inspection data has been automatically saved</span>
          </div>
          <div class="flex align-items-center gap-3">
            <i class="pi pi-video text-blue-500"></i>
            <span>Recording will be processed and available shortly (if enabled)</span>
          </div>
          <div class="flex align-items-center gap-3">
            <i class="pi pi-envelope text-orange-500"></i>
            <span>You will receive a summary report via email</span>
          </div>
          <div class="flex align-items-center gap-3">
            <i class="pi pi-database text-purple-500"></i>
            <span>All captured media is securely stored in your account</span>
          </div>
        </div>
      </div>
      
      <div class="steps-content" *ngIf="isInspector">
        <div class="flex flex-column gap-3">
          <div class="flex align-items-center gap-3">
            <i class="pi pi-check-circle text-green-500"></i>
            <span>Your inspection contribution has been recorded</span>
          </div>
          <div class="flex align-items-center gap-3">
            <i class="pi pi-mobile text-blue-500"></i>
            <span>All photos and videos have been uploaded successfully</span>
          </div>
          <div class="flex align-items-center gap-3">
            <i class="pi pi-users text-orange-500"></i>
            <span>The coordinator has access to all session data</span>
          </div>
          <div class="flex align-items-center gap-3">
            <i class="pi pi-phone text-purple-500"></i>
            <span>Contact your coordinator if you have any questions</span>
          </div>
        </div>
      </div>
    </p-card>

    <!-- Final Message -->
    <p-card>
      <div class="text-center">
        <p-avatar icon="pi pi-check" size="xlarge" styleClass="success-avatar mb-3"></p-avatar>
        <h3 class="text-green-700 mb-2">Session Complete</h3>
        <p class="text-color-secondary mb-0">This session has ended successfully. You can now safely close this page or navigate away.</p>
      </div>
    </p-card>
  </div>
</div>
